import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";

const STAGE_ORDER = ["Potential", "Engaged", "Negotiation", "Converted", "Lost"];

const STAGE_COLORS = {
  Potential: "#94a3b8",     // slate-400
  Engaged: "#3b82f6",       // blue-500
  Negotiation: "#6366f1",   // indigo-500
  Converted: "#10b981",     // emerald-500
  Lost: "#ef4444",          // red-500
};

const CHART_MARGIN = { top: 8, right: 32, left: 0, bottom: 8 };
const TOOLTIP_CURSOR = { fill: "rgba(15,23,42,0.04)" };
const BAR_RADIUS = [0, 6, 6, 0];

function TooltipBox({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { stage, value } = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-md px-3 py-2 shadow-sm">
      <div className="text-xs font-medium text-slate-500">{stage}</div>
      <div className="text-sm font-semibold text-slate-900">{value} customers</div>
    </div>
  );
}

export default function PipelineFunnelChart({ statusBreakdown }) {
  const data = useMemo(() => {
    return STAGE_ORDER.map((stage) => ({
      stage,
      value: Number(statusBreakdown?.[stage] || 0),
      fill: STAGE_COLORS[stage],
    }));
  }, [statusBreakdown]);

  const hasData = data.some((d) => d.value > 0);

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-6"
      data-testid="pipeline-funnel-chart"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">
            Pipeline funnel
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">
            Customer count by lifecycle stage
          </div>
        </div>
      </div>
      {hasData ? (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={CHART_MARGIN}
            >
              <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="stage"
                stroke="#475569"
                fontSize={12}
                width={96}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<TooltipBox />} cursor={TOOLTIP_CURSOR} />
              <Bar dataKey="value" radius={BAR_RADIUS}>
                {data.map((entry) => (
                  <Cell key={entry.stage} fill={entry.fill} />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  fill="#0f172a"
                  fontSize={12}
                  fontWeight={600}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">
          No pipeline data yet
        </div>
      )}
    </div>
  );
}
